/** Dev21 — Footer. Minimal closing note with social links. */

import React from 'react';
import { Instagram, Facebook, MessageCircle, Globe } from 'lucide-react';
import { Reveal } from '../components/primitives';
import type { Dev21Content } from '../content';

/** Map a social label to a lucide icon, with a sensible default. */
function iconFor(label: string) {
  const key = label.toLowerCase();
  if (key.includes('insta')) return Instagram;
  if (key.includes('face')) return Facebook;
  if (key.includes('whats') || key.includes('message')) return MessageCircle;
  return Globe;
}

export function FooterSection({
  content,
  isPremium,
}: {
  content: Dev21Content['footer'];
  isPremium?: boolean;
}) {
  return (
    <footer className="relative w-full bg-[var(--d21-ink)] px-5 py-16 text-center sm:px-8">
      <Reveal className="mx-auto flex max-w-2xl flex-col items-center gap-6">
        <span className="h-px w-14 bg-[var(--d21-accent)]" />
        <p className="text-base leading-relaxed text-[var(--d21-cream)]/70">
          {content.message}
        </p>
        <p className="font-serif text-3xl text-[var(--d21-accent-soft)]">
          {content.signature}
        </p>

        {content.socials.length ? (
          <div className="flex items-center gap-3">
            {content.socials.map((social) => {
              const Icon = iconFor(social.label);
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 text-[var(--d21-cream)]/70 transition hover:-translate-y-0.5 hover:border-[var(--d21-accent)] hover:text-[var(--d21-accent)]"
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        ) : null}

        {!isPremium && (
          <p className="mt-2 text-[0.66rem] uppercase tracking-[0.3em] text-[var(--d21-cream)]/35">
            A digital invitation by InviteCloud
          </p>
        )}
      </Reveal>
    </footer>
  );
}
